import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  DialogTrigger,
  makeStyles,
} from "@fluentui/react-components";
import { Dismiss24Regular } from "@fluentui/react-icons";
import { AppSpinner } from "components/AppSpinner/AppSpinner";
import { PrimaryButton } from "components/PrimaryButton/PrimaryButton";
import { Form, Formik } from "formik";
import { devices } from "library/constants";
import { ECheckedValue, IBenefitCategory } from "library/types";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useCreateBenefitCategory } from "state/queries/useCreateBenefitCategory";
import { useUpdateBenefitCategory } from "state/queries/useUpdateBenefitCategory";
import styled from "styled-components";
import * as yup from "yup";
import { BenefitFormItems } from "./BenefitFormItems";

type Props = {
  open: boolean;
  setOpen: (open: boolean) => void;
  benefitCategory?: IBenefitCategory;
  countryCode: number;
  clientInternalNumberGOS?: number;
  benefitCategoryParentTag?: number | null;
  rootCountryCode?: boolean;
};

const useStyles = makeStyles({
  body: {
    display: "flex",
    flexDirection: "column",
    width: "860px",
  },
  content: {
    maxHeight: "90vh",
    scrollbarWidth: "thin",
  },
  actions: {
    display: "flex",
    flexDirection: "row",
    width: "100%",
    paddingTop: "10px",
    "@media (max-width: 600px)": {
      flexDirection: "row",
    },
  },
});

export const BenefitCategoryDialog = ({
  open,
  setOpen,
  benefitCategory,
  countryCode,
  clientInternalNumberGOS = 0,
  rootCountryCode,
}: Props) => {
  const { t } = useTranslation();

  const { mutate: onCreateBenefitCategory, isLoading: createInProgress } =
    useCreateBenefitCategory();

  const { mutate: onUpdateBenefitCategory, isLoading: updateInProgress } =
    useUpdateBenefitCategory();

  const title = benefitCategory
    ? t("hbh.editBenefitCategory.label", {
        category: benefitCategory.name,
      })
    : t("hbh.addBenefitCategory.label");

  const styles = useStyles();

  const initialValues = React.useMemo(() => {
    return {
      name: benefitCategory?.name || "",

      consentsNeeded: benefitCategory?.consentsNeeded
        ? ECheckedValue.Yes
        : ECheckedValue.No,

      isAutoEnrollment: benefitCategory?.isAutoEnrollment
        ? ECheckedValue.Yes
        : ECheckedValue.No,
      coverPhoto: benefitCategory?.backgroundPicturePath
        ? {
            name: benefitCategory?.backgroundPictureName,
            path: benefitCategory?.backgroundPicturePath,
            base64String: benefitCategory?.backgroundPictureContent,
            type: benefitCategory?.backgroundPictureMimeType,
          }
        : null,
      description: benefitCategory?.description || "",
      attachments: benefitCategory?.attachments || [],
    };
  }, [
    benefitCategory?.name,
    benefitCategory?.consentsNeeded,
    benefitCategory?.isAutoEnrollment,
    benefitCategory?.backgroundPicturePath,
    benefitCategory?.backgroundPictureName,
    benefitCategory?.backgroundPictureContent,
    benefitCategory?.backgroundPictureMimeType,
    benefitCategory?.description,
    benefitCategory?.attachments,
  ]);

  const handleSubmit = (values) => {
    if (benefitCategory?.benefitCategoryTag) {
      onUpdateBenefitCategory(
        {
          countryCode: Number(benefitCategory.countryCode),
          clientInternalNumberGOS: Number(
            benefitCategory.clientInternalNumberGos
          ),
          benefitCategoryTag: benefitCategory?.benefitCategoryTag,

          payload: {
            ...benefitCategory,

            name: values?.name || "",

            consentsNeeded: values?.consentsNeeded === ECheckedValue.Yes,

            isAutoEnrollment: values?.isAutoEnrollment === ECheckedValue.Yes,

            description: values?.description || "",
            backgroundPictureContent: values.coverPhoto?.base64String,
            backgroundPictureName: values.coverPhoto?.name,
            backgroundPicturePath: values.coverPhoto?.path,
            backgroundPictureMimeType: values.coverPhoto?.type,
            ...(values.coverPhoto?.base64String && {
              backgroundPictureContent: values.coverPhoto?.base64String,
              backgroundPictureName: values.coverPhoto?.name,
              backgroundPicturePath: values.coverPhoto?.path,
              backgroundPictureMimeType: values.coverPhoto?.type,
            }),
            attachments: values?.attachments ? values.attachments : [],
          },
        },
        {
          onSuccess: () => {
            toast.success(t("hbh.benefitCategoryUpdatedSuccessfully.label"));
            setOpen(false);
          },
        }
      );
      return;
    }

    onCreateBenefitCategory(
      {
        countryCode,
        clientInternalNumberGOS,
        payload: {
          ...values,

          consentsNeeded: values?.consentsNeeded === ECheckedValue.Yes,

          isAutoEnrollment: values?.isAutoEnrollment === ECheckedValue.Yes,
          backgroundPictureName: values?.coverPhoto?.name,
          backgroundPictureContent: values?.coverPhoto?.base64String,
          backgroundPictureMimeType: values?.coverPhoto?.type,
          ...(!values?.coverPhoto?.base64String && {
            backgroundPicturePath: values?.coverPhoto?.path,
          }),
        },
      },
      {
        onSuccess() {
          toast.success(t("hbh.benefitCategoryAddedSuccessfully.label"));
          setOpen(false);
        },
        onError() {
          toast.error(t("greco.error"));
        },
      }
    );
  };

  const validationSchema = yup.object().shape({
    name: yup.string().min(2).max(128).required(),
    description: yup.string().min(2).max(4000).required(),
    consentsNeeded: yup.mixed().required(),
    isAutoEnrollment: yup.mixed().required(),
  });

  const isLoading = createInProgress || updateInProgress;

  return (
    <>
      <Dialog
        modalType="alert"
        open={open}
        onOpenChange={(event, data) => setOpen(data.open)}
      >
        <StyledDialogSurface
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          <>
            {isLoading && <AppSpinner />}

            <Formik
              validationSchema={validationSchema}
              initialValues={initialValues}
              enableReinitialize
              onSubmit={(values, actions) => {
                handleSubmit(values);
                actions.setSubmitting(false);
              }}
            >
              {({ errors }) => {
                const hasErrors = !!Object.keys(errors)?.length;
                return (
                  <Form>
                    <DialogBody className={styles.content}>
                      <DialogTitle
                        action={
                          <DialogTrigger action="close">
                            <Button
                              appearance="subtle"
                              aria-label="close"
                              icon={<Dismiss24Regular />}
                            />
                          </DialogTrigger>
                        }
                      >
                        {title}
                      </DialogTitle>
                      <DialogContent className={styles.content}>
                        <BenefitFormItems
                          benefitCategory={benefitCategory}
                          countryCode={countryCode}
                          clientInternalNumberGOS={clientInternalNumberGOS}
                          rootCountryCode={rootCountryCode}
                        />
                      </DialogContent>
                    </DialogBody>
                    <DialogActions
                      className={benefitCategory ? styles.actions : ""}
                    >
                      <PrimaryButton
                        data-help="benefit-category-dialog-save"
                        disabled={isLoading || hasErrors}
                        style={{
                          paddingLeft: "25px",
                          paddingRight: "25px",
                          marginLeft: "auto",
                        }}
                      >
                        {t("greco.save")}
                      </PrimaryButton>
                    </DialogActions>
                  </Form>
                );
              }}
            </Formik>
          </>
        </StyledDialogSurface>
      </Dialog>
    </>
  );
};

const StyledDialogSurface = styled(DialogSurface)`
  margin-left: 10px;
  margin-right: 10px;
  max-height: 98vh;
  padding: 15px;
  width: 95%;
  min-width: 95%;

  @media only screen and ${devices.md} {
    width: 800px;
    min-width: 800px;
    padding: 24px;
    margin-left: auto;
    margin-right: auto;
  }
`;
